import { useEffect, useState } from "react";
import { api, errorMessage, post } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

type GuardianLink = {
  relationshipId?: string | null;
  userId: string | null;
  name: string;
  label: string;
  primary?: boolean;
  emergency?: boolean;
};

type GuardianHit = { id: string; email?: string | null; first_name?: string | null; last_name?: string | null };

const GUARDIAN_TYPE_OPTIONS = [
  { value: "father", label: "Father" },
  { value: "mother", label: "Mother" },
  { value: "grandfather", label: "Grandfather" },
  { value: "grandmother", label: "Grandmother" },
  { value: "step_father", label: "Step Father" },
  { value: "step_mother", label: "Step Mother" },
  { value: "foster_parent", label: "Foster Parent" },
  { value: "other", label: "Other" },
] as const;

/** Add a guardian to yourself. The control stays off without people.AddSelfGuardianRelationships. */
export default function StudentGuardians() {
  const { capabilities } = useAuth();
  const allowed = Boolean(capabilities?.addSelfGuardianRelationships);
  const canSearch = Boolean(capabilities?.searchPeople);
  const [links, setLinks] = useState<GuardianLink[] | null>(null);
  const [mode, setMode] = useState<"search" | "create">("create");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<GuardianHit[]>([]);
  const [searched, setSearched] = useState(false);
  const [picked, setPicked] = useState<GuardianHit | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [guardianType, setGuardianType] = useState("mother");
  const [primary, setPrimary] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!allowed) return undefined;
    let cancel = false;
    api<{ success: boolean; data: GuardianLink[] }>("/api/student/guardians")
      .then((env) => {
        if (!cancel) setLinks(env.data || []);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [allowed]);

  if (!allowed) return null;
  const hasPrimary = Boolean(links?.some((link) => link.primary));
  const ready = mode === "create" ? Boolean(firstName.trim() && lastName.trim()) : Boolean(picked);

  return (
    <section className="student-card parent-form-card">
      <h2 className="parent-card-title">Guardians</h2>
      {links === null && !error ? <p className="parent-form-soon">Loading guardians</p> : null}
      {links && links.length ? (
        <ul className="parent-people">
          {links.map((link) => (
            <li key={link.relationshipId || link.userId || link.name}>
              {[link.name, link.label, link.primary ? "Primary" : "", link.emergency ? "Emergency" : ""].filter(Boolean).join(" · ")}
            </li>
          ))}
        </ul>
      ) : null}
      <form
        className="parent-relationship"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || busy) return;
          setBusy(true);
          setError("");
          const body =
            mode === "create"
              ? { mode: "create", firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), guardianType, primary, emergency }
              : { mode: "search", userId: picked?.id, guardianType, primary, emergency };
          post<{ success: boolean; data: GuardianLink }>("/api/student/guardians", body)
            .then((env) => {
              if (!env.data) return;
              setLinks((current) => [...(current || []).filter((row) => row.relationshipId !== env.data.relationshipId), env.data]);
              setQuery("");
              setHits([]);
              setSearched(false);
              setPicked(null);
              setFirstName("");
              setLastName("");
              setEmail("");
              setPrimary(false);
              setEmergency(false);
            })
            .catch((err) => setError(errorMessage(err)))
            .finally(() => setBusy(false));
        }}
      >
        <p className="parent-form-lede">Add a guardian</p>
        {canSearch ? (
          <div className="parent-relationship__modes" role="group" aria-label="How to add a guardian">
            <button type="button" className={mode === "search" ? "is-on" : ""} disabled={busy} onClick={() => setMode("search")}>
              Search existing
            </button>
            <button type="button" className={mode === "create" ? "is-on" : ""} disabled={busy} onClick={() => setMode("create")}>
              Create new
            </button>
          </div>
        ) : null}
        {canSearch && mode === "search" ? (
          <>
            <div className="parent-relationship__search">
              <input className="student-input" value={query} placeholder="Name or email" aria-label="Find a guardian" disabled={busy} onChange={(event) => setQuery(event.target.value)} />
              <button
                type="button"
                className="student-btn student-btn--ghost"
                disabled={busy || !query.trim()}
                onClick={() => {
                  setBusy(true);
                  setError("");
                  setPicked(null);
                  api<{ success: boolean; data: GuardianHit[] }>(`/api/student/guardians/search?q=${encodeURIComponent(query.trim())}`)
                    .then((env) => {
                      setHits(env.data || []);
                      setSearched(true);
                    })
                    .catch((err) => setError(errorMessage(err)))
                    .finally(() => setBusy(false));
                }}
              >
                Search
              </button>
            </div>
            {searched && !hits.length ? <p className="parent-form-soon">No matches.</p> : null}
            {hits.length ? (
              <div className="parent-relationship__hits">
                {hits.map((hit) => {
                  const name = [hit.first_name, hit.last_name].filter(Boolean).join(" ").trim() || hit.email || "Unnamed";
                  return (
                    <button key={hit.id} type="button" className={picked?.id === hit.id ? "is-on" : ""} disabled={busy} onClick={() => setPicked(hit)}>
                      {name}
                      {hit.email ? <span>{hit.email}</span> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </>
        ) : (
          <div className="parent-relationship__create">
            <input className="student-input" value={firstName} placeholder="First name" aria-label="First name" disabled={busy} onChange={(event) => setFirstName(event.target.value)} />
            <input className="student-input" value={lastName} placeholder="Last name" aria-label="Last name" disabled={busy} onChange={(event) => setLastName(event.target.value)} />
            <input className="student-input" type="email" value={email} placeholder="Email" aria-label="Email" disabled={busy} onChange={(event) => setEmail(event.target.value)} />
          </div>
        )}
        <label className="parent-field" htmlFor="student-add-guardian-type">
          <span>Relationship</span>
          <select id="student-add-guardian-type" className="student-input" value={guardianType} disabled={busy} onChange={(event) => setGuardianType(event.target.value)}>
            {GUARDIAN_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <div className="parent-relationship__checks">
          <label className="parent-relationship__check">
            <input type="checkbox" checked={primary} disabled={busy || hasPrimary} onChange={(event) => setPrimary(event.target.checked)} />
            Primary{hasPrimary ? " already set" : ""}
          </label>
          <label className="parent-relationship__check">
            <input type="checkbox" checked={emergency} disabled={busy} onChange={(event) => setEmergency(event.target.checked)} />
            Emergency
          </label>
        </div>
        {error ? (
          <div className="student-inline-error" role="alert">
            {error}
          </div>
        ) : null}
        <div className="parent-form-actions">
          <button type="submit" className="student-btn" disabled={busy || !ready}>
            {busy ? "Linking" : "Link"}
          </button>
        </div>
      </form>
    </section>
  );
}
